import React, {
  useMemo,
  useCallback,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { QueryLoader, Text, Button, HelpIndicator, Flex } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import { useGetProfilesHierarchyQuery } from "../../graphql/supergraphHooks";
import { resolvedAccountType } from "../../utils/account_types";
import { ButtonWrapper } from "../common/ButtonWrapper";
import { computeChildOwnerCountByProfileId } from "../common/ProfileManagement/direct_owner_count";
import { textColorNeutral800 } from "../common/style_vanilla.css";
import { TreeComponent } from "../common/TreeComponent";
import { Node } from "../common/TreeComponent/types";
import { isProfileCompleted } from "../ProfileActionOverview/utils";
import { completionText, completionTooltipText } from "../utils";
import { ProfileTreeCard } from "./ProfileTreeCard";
import {
  completeButtonContainer,
  edgeLabel,
  rootEdgeLabel,
  stepDescription,
} from "./style_vanilla.css";
import { ProfileTreeNode, TreeProfile } from "./types";
import { buildHierarchy, extractChildProfiles, pageSummary, provideLegalName } from "./utils";

/**
 * A hierarchical tree visualization component that displays ownership relationships
 * between entities in a compliance case. The component fetches profile data from GraphQL,
 * processes it into a hierarchical structure, and renders it as an interactive tree.
 *
 * The tree represents the ownership hierarchy of an organization, showing:
 */
export const ProfileTree = ({ onStepCompletion }: { onStepCompletion: () => void }) => {
  const { basePath, caseId, programId, caseType, rootProfileId } = useComplianceCaseContext();
  const profileTreeRef = useRef<HTMLDivElement>(null);
  const [treeBounds, setTreeBounds] = useState<DOMRect>();
  const profileResults = useGetProfilesHierarchyQuery({
    fetchPolicy: "no-cache",
    variables: {
      caseId: caseId,
    },
  });

  useLayoutEffect(() => {
    const measureBounds = () => {
      if (profileTreeRef.current) {
        const newBounds = profileTreeRef.current.getBoundingClientRect();
        setTreeBounds((prev) =>
          !prev ||
          Math.abs(prev.width - newBounds.width) > 1 ||
          Math.abs(prev.height - newBounds.height) > 1
            ? newBounds
            : prev,
        );
      }
    };

    measureBounds();

    const timeoutId = setTimeout(measureBounds, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  /**
   * Creates edge labels showing ownership percentages with hover tooltip
   *
   * This function generates a label to display on the connecting edge between
   * parent and child nodes, showing the ownership percentage when available.
   * On hover, shows the precise percentage value to 2 decimal places.
   * The displayed percentage is rounded to the nearest whole number.
   *
   * @param percentOwnership - Raw percentage of ownership to display
   * @param nodeId - The node ID for generating unique tooltip IDs
   * @param className - CSS class to apply to the label
   * @returns React component for the edge label
   */
  const provideEdgeLabel = useCallback(
    (percentOwnership: number, nodeId?: number, className: string = edgeLabel): ReactNode => {
      const roundedPercentage = Math.round(percentOwnership);
      const precisePercentage = percentOwnership.toFixed(2);

      return (
        <HelpIndicator content={`${precisePercentage}%`} id={nodeId}>
          <Text variant="strong-sm" className={className}>
            {roundedPercentage}%
          </Text>
        </HelpIndicator>
      );
    },
    [],
  );

  /**
   * Memoized extraction of child profiles with parent relationships
   *
   * This processes the raw GraphQL data to:
   * 1. Filter out null profiles from the array
   * 2. Extract only profiles that have relevant parent relationships
   *
   * The result is a clean array of profiles that will be used to build the hierarchy.
   */
  const allChildProfiles = useMemo(
    () =>
      extractChildProfiles(
        profileResults.data?.complianceCase?.profiles?.filter(
          (profile): profile is TreeProfile => profile !== null,
        ) || [],
      ),
    [profileResults],
  );

  /**
   * Memoized extraction of the main investing profile
   *
   * This will be used as the root node of the ownership tree.
   */
  const investingProfile = useMemo(
    () => profileResults.data?.complianceCase?.investingProfile,
    [profileResults],
  );

  const childOwnerCountByProfileId = useMemo(
    () => computeChildOwnerCountByProfileId(profileResults.data?.complianceCase?.profiles),
    [profileResults.data?.complianceCase?.profiles],
  );

  /**
   * Transforms a hierarchical profile node into the Tree component's Node format
   *
   * This recursive function processes each node in the hierarchy and:
   * 1. Converts IDs to strings (required by the TreeComponent)
   * 2. Generates the visual content for the node
   * 3. Adds edge labels with ownership percentages
   * 4. Recursively processes child nodes
   *
   * @param node - The hierarchical profile node to transform
   * @returns Node structure compatible with the TreeComponent or null for invalid nodes
   */
  const stripProfileNode = useCallback(
    (node: ProfileTreeNode | null): Node | null => {
      if (!node) return null;

      return {
        id: node.id.toString(),
        content: (
          <ProfileTreeCard
            profileInfo={node.profileInfo}
            basePath={basePath}
            rootAccountType={resolvedAccountType(investingProfile)}
            rootRulesEngine={investingProfile?.rulesEngine}
            childOwnersCount={childOwnerCountByProfileId.get(node.id)}
          />
        ),
        parentId: node.parentId?.toString(),
        edgeLabel:
          node.percentageOwnership && provideEdgeLabel(node.percentageOwnership, node.id),
        rootEdgeLabel:
          node.rootPercentageOwnership &&
          provideEdgeLabel(node.rootPercentageOwnership, node.id, rootEdgeLabel),
        leafNodes: Array.isArray(node.children)
          ? node.children
              .map((child) => stripProfileNode(child))
              .filter((child): child is Node => child !== null)
          : [],
      };
    },
    [basePath, investingProfile, provideEdgeLabel, childOwnerCountByProfileId],
  );

  /**
   * Constructs the complete hierarchy data for display
   *
   * This combines all the processed data to:
   * 1. Build the hierarchical structure using the profile data
   * 2. Transform it into the format required by the TreeComponent
   *
   * Returns null if the investing profile is missing, which will trigger error handling.
   */
  const displayData = useMemo(() => {
    if (!investingProfile) return null;
    const hierarchy = buildHierarchy(allChildProfiles, investingProfile);
    return hierarchy ? stripProfileNode(hierarchy) : null;
  }, [allChildProfiles, investingProfile, stripProfileNode]);

  /**
   * Gets the organization name to display in the header
   *
   * Uses the legal name of the investing profile or a placeholder if not available.
   */
  const orgName = useMemo(() => provideLegalName(investingProfile), [investingProfile]);

  const allProfilesCompleted = useMemo(() => {
    const rootProfile = {
      id: rootProfileId,
      accountType: resolvedAccountType(investingProfile),
      rulesEngine: investingProfile?.rulesEngine,
    };

    const investingProfileWithChildOwnersCount = investingProfile
      ? {
          ...investingProfile,
          childOwnersCount: childOwnerCountByProfileId.get(rootProfileId) ?? 0,
        }
      : investingProfile;

    const rootProfileCompleted = isProfileCompleted(
      caseType,
      rootProfile,
      investingProfileWithChildOwnersCount,
      programId,
    );

    const childrenCompleted = profileResults.data?.complianceCase?.profiles
      ?.filter((profile): profile is NonNullable<typeof profile> => profile !== null)
      ?.every((profile) => {
        const childOwnersCount = childOwnerCountByProfileId.get(profile.id);
        const profileWithCount = { ...profile, childOwnersCount };
        return isProfileCompleted(caseType, rootProfile, profileWithCount, programId);
      });

    return rootProfileCompleted && childrenCompleted;
  }, [
    rootProfileId,
    investingProfile,
    caseType,
    programId,
    profileResults.data?.complianceCase?.profiles,
    childOwnerCountByProfileId,
  ]);

  return (
    <QueryLoader result={profileResults}>
      <div ref={profileTreeRef}>
        <Text variant="h3">{`${orgName} organization structure`}</Text>
        <Text variant="regular-md" className={stepDescription}>
          {pageSummary(caseType, orgName)}
        </Text>
        <div role="tree">
          {displayData ? (
            <TreeComponent
              data={displayData}
              rootEdgeStyle={{ dashWidth: 3, dashGap: 3 }}
              treeConfig={{ nodeMaxWidth: treeBounds?.width }}
            />
          ) : (
            <Text variant="regular-md" className={textColorNeutral800}>
              No ownership structure data is available for this organization.
            </Text>
          )}
        </div>
        <Flex className={completeButtonContainer}>
          <ButtonWrapper
            tooltip={completionTooltipText(caseType)}
            id="help_indicator"
            disabled={!allProfilesCompleted}
          >
            <Button
              variant="primary"
              onClick={() => {
                onStepCompletion();
              }}
              data-testid="profile-tree-complete-button"
              disabled={!allProfilesCompleted}
            >
              {completionText(caseType)}
            </Button>
          </ButtonWrapper>
        </Flex>
      </div>
    </QueryLoader>
  );
};
